import { type ReactNode, useLayoutEffect, useMemo, useRef, useState } from "react";
import { filterMentionUsers, type MentionPerson, mentionHits, mentionQuery } from "../../lib/mentions";
import { personLabel } from "../../lib/people";
import type { UserBadgeInfo } from "../../types";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Textarea } from "../ui/textarea";

function initials(user: UserBadgeInfo): string {
  return personLabel(user)
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/** Where the caret's line sits inside the textarea, so the mention list can hang under it. */
function caretOffset(textarea: HTMLTextAreaElement): { top: number; left: number; lineHeight: number } {
  const style = getComputedStyle(textarea);
  const mirror = document.createElement("div");
  mirror.style.position = "absolute";
  mirror.style.visibility = "hidden";
  mirror.style.whiteSpace = "pre-wrap";
  mirror.style.overflowWrap = "break-word";
  mirror.style.width = `${textarea.clientWidth}px`;
  for (const prop of [
    "fontFamily",
    "fontSize",
    "fontWeight",
    "fontStyle",
    "letterSpacing",
    "lineHeight",
    "textTransform",
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft",
    "borderTopWidth",
    "borderRightWidth",
    "borderBottomWidth",
    "borderLeftWidth",
    "boxSizing",
  ] as const) {
    mirror.style[prop] = style[prop];
  }
  mirror.textContent = textarea.value.slice(0, textarea.selectionStart ?? 0);
  const marker = document.createElement("span");
  marker.textContent = "\u200b";
  mirror.appendChild(marker);
  document.body.appendChild(mirror);
  const top = marker.offsetTop - textarea.scrollTop;
  const left = marker.offsetLeft - textarea.scrollLeft;
  const lineHeight = marker.offsetHeight || Number.parseFloat(style.lineHeight) || 20;
  mirror.remove();
  return { top, left, lineHeight };
}

/** The draft with each matched `@Name` wrapped, drawn behind the transparent textarea text. */
function highlightedDraft(value: string, people: MentionPerson[]): ReactNode[] {
  const parts: ReactNode[] = [];
  let index = 0;
  for (const hit of mentionHits(value, people)) {
    if (hit.start > index) parts.push(value.slice(index, hit.start));
    parts.push(
      <span key={hit.start} className="rounded-sm bg-primary/10 text-primary">
        {value.slice(hit.start, hit.end)}
      </span>,
    );
    index = hit.end;
  }
  parts.push(value.slice(index));
  // A trailing newline only takes up a line in the backdrop when something follows it.
  parts.push("\u200b");
  return parts;
}

/**
 * Comment box with an `@` picker. Typing filters the list. Tab or Enter inserts
 * the highlighted person, which starts as the first match. A click does too.
 */
export function MentionField({
  value,
  onChange,
  onSubmit,
  users,
  disabled,
  placeholder = "Leave a comment… @ to mention someone",
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  users: UserBadgeInfo[];
  disabled?: boolean;
  placeholder?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const backdrop = useRef<HTMLDivElement>(null);
  const [caret, setCaret] = useState(0);
  const [active, setActive] = useState(0);
  const [closedFor, setClosedFor] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const pending = mentionQuery(value, caret);
  const matches = pending ? filterMentionUsers(users, pending.query) : [];
  const token = pending ? `${pending.start}:${pending.query}` : null;
  const open = Boolean(pending && matches.length > 0 && closedFor !== token);
  const listId = "comment-mentions";
  const highlighted = matches[Math.min(active, Math.max(matches.length - 1, 0))];
  const people = useMemo(
    () =>
      users
        .filter((user) => user.kind === "human" && user.active && personLabel(user) !== "Someone")
        .map((user) => ({ id: user.id, displayName: personLabel(user) })),
    [users],
  );

  useLayoutEffect(() => {
    const el = ref.current;
    if (!open || !el || el.value !== value) return;
    const pos = caretOffset(el);
    const width = 256;
    const left = Math.max(8, Math.min(pos.left, el.clientWidth - width - 8));
    setAnchor({ top: pos.top + pos.lineHeight + 4, left: caret >= 0 ? left : 8 });
  }, [open, value, caret]);

  const syncCaret = (el: HTMLTextAreaElement) => setCaret(el.selectionStart ?? 0);

  const choose = (user: UserBadgeInfo) => {
    if (!pending) return;
    const name = personLabel(user);
    const next = `${value.slice(0, pending.start)}@${name} ${value.slice(caret)}`;
    const cursor = pending.start + name.length + 2;
    const el = ref.current;
    onChange(next);
    setCaret(cursor);
    setActive(0);
    // Set the caret before keyup, which would otherwise read the old position
    // and reopen the list on the half-typed query.
    if (el) {
      el.value = next;
      el.setSelectionRange(cursor, cursor);
    }
  };

  return (
    <div className="relative">
      <div
        ref={backdrop}
        aria-hidden
        className={`pointer-events-none absolute inset-0 overflow-hidden px-3 py-2.5 text-base break-words whitespace-pre-wrap text-foreground md:text-sm ${
          disabled ? "opacity-50" : ""
        }`}
      >
        {highlightedDraft(value, people)}
      </div>
      <Textarea
        ref={ref}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.value);
          syncCaret(e.target);
          setActive(0);
        }}
        placeholder={placeholder}
        aria-label="Comment"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && highlighted ? `${listId}-${highlighted.id}` : undefined}
        rows={3}
        className="relative max-h-80 min-h-20 resize-none rounded-b-none border-0 bg-transparent px-3 py-2.5 text-transparent caret-foreground shadow-none focus-visible:ring-0 disabled:bg-transparent dark:bg-transparent dark:disabled:bg-transparent"
        onScroll={(e) => {
          if (backdrop.current) backdrop.current.scrollTop = e.currentTarget.scrollTop;
        }}
        onClick={(e) => syncCaret(e.currentTarget)}
        onKeyUp={(e) => syncCaret(e.currentTarget)}
        onSelect={(e) => syncCaret(e.currentTarget)}
        onKeyDown={(e) => {
          if (open && matches.length > 0) {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActive((index) => (index + 1) % matches.length);
              return;
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((index) => (index - 1 + matches.length) % matches.length);
              return;
            }
            if (e.key === "Escape") {
              e.preventDefault();
              setClosedFor(token);
              return;
            }
            if ((e.key === "Enter" && !e.metaKey && !e.ctrlKey) || e.key === "Tab") {
              e.preventDefault();
              const pick = matches[Math.min(active, matches.length - 1)] ?? matches[0];
              if (pick) choose(pick);
              return;
            }
          }
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            onSubmit();
          }
        }}
      />
      {open && anchor && (
        <div
          id={listId}
          role="listbox"
          aria-label="People"
          className="absolute z-30 max-h-56 w-64 overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
          style={{ top: anchor.top, left: anchor.left }}
        >
          {matches.map((user, index) => {
            const selected = user.id === highlighted?.id;
            return (
              <button
                key={user.id}
                id={`${listId}-${user.id}`}
                type="button"
                role="option"
                aria-selected={selected}
                className={`flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm ${
                  selected ? "bg-accent text-accent-foreground" : "hover:bg-muted"
                }`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(user);
                }}
                onMouseEnter={() => setActive(index)}
              >
                <Avatar className="size-6">
                  <AvatarFallback className="text-[10px]">{initials(user)}</AvatarFallback>
                </Avatar>
                <span className="min-w-0">
                  <span className="block truncate font-medium">{personLabel(user)}</span>
                  {user.email.includes("@") && (
                    <span className="block truncate text-xs text-muted-foreground">{user.email}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
